const MONTH_LABELS_PT_BR = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
];

function parseBRDateParts(br) {
  if (!br) return null;

  const [day, month, year] = String(br).split("/").map(Number);
  if (!day || !month || !year) return null;

  return {
    day,
    month,
    year,
    iso: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
  };
}

function formatMonthLabel(year, month) {
  return `${MONTH_LABELS_PT_BR[month - 1]}/${year}`;
}

function sortParsedDates(dates = []) {
  return dates
    .map((date) => {
      const parsed = parseBRDateParts(date);
      return parsed ? { date, parsed } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.parsed.iso.localeCompare(b.parsed.iso));
}

function getMonthBucket(date) {
  const parsed = parseBRDateParts(date);
  if (!parsed) return null;

  return {
    key: `${parsed.year}-${String(parsed.month).padStart(2, "0")}`,
    label: formatMonthLabel(parsed.year, parsed.month),
  };
}

function pickPeriodTotal(value) {
  const total = Number(value?.total ?? value ?? 0);
  return Number.isFinite(total) ? total : 0;
}

export function mapPerDayToSeries(data, options = {}) {
  if (!data || typeof data !== "object") {
    return { categories: [], values: [] };
  }

  const { groupByMonth = false } = options;
  const dates = sortParsedDates(Object.keys(data));

  if (!dates.length) {
    return { categories: [], values: [] };
  }

  if (!groupByMonth) {
    return {
      categories: dates.map(({ date }) => date),
      values: dates.map(({ date }) => pickPeriodTotal(data[date])),
    };
  }

  const monthlyTotals = new Map();

  dates.forEach(({ date }) => {
    const month = getMonthBucket(date);
    if (!month) return;

    const current = monthlyTotals.get(month.key) || { label: month.label, total: 0 };
    current.total += pickPeriodTotal(data[date]);
    monthlyTotals.set(month.key, current);
  });

  const sortedMonths = Array.from(monthlyTotals.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, value]) => value);

  return {
    categories: sortedMonths.map((month) => month.label),
    values: sortedMonths.map((month) => month.total),
  };
}

export function mapQualitativePerDayToSeries(data, options = {}) {
  if (!data) {
    return { categories: [], total: [], positive: [], neutral: [], negative: [] };
  }

  const { groupByMonth = false } = options;

  const dates = sortParsedDates(Object.keys(data)).map(({ date }) => date);

  if (!groupByMonth) {
    return {
      categories: dates,
      total: dates.map((d) => data[d]?.total ?? 0),
      positive: dates.map((d) => data[d]?.positive ?? 0),
      neutral: dates.map((d) => data[d]?.neutral ?? 0),
      negative: dates.map((d) => data[d]?.negative ?? 0),
    };
  }

  const monthlyTotals = new Map();

  dates.forEach((date) => {
    const month = getMonthBucket(date);
    if (!month) return;

    const current = monthlyTotals.get(month.key) || {
      label: month.label,
      total: 0,
      positive: 0,
      neutral: 0,
      negative: 0,
    };

    current.total += toNum(data[date]?.total);
    current.positive += toNum(data[date]?.positive);
    current.neutral += toNum(data[date]?.neutral);
    current.negative += toNum(data[date]?.negative);

    monthlyTotals.set(month.key, current);
  });

  const months = Array.from(monthlyTotals.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, value]) => value);

  return {
    categories: months.map((month) => month.label),
    total: months.map((month) => month.total),
    positive: months.map((month) => month.positive),
    neutral: months.map((month) => month.neutral),
    negative: months.map((month) => month.negative),
  };
}

function normalizeToArray(input) {
  if (!input) return [];

  if (Array.isArray(input)) return input;
  if (Array.isArray(input?.results)) return input.results;

  if (typeof input === "object") {
    return Object.entries(input).map(([key, val]) => ({
      name: key,
      ...(val || {}),
    }));
  }

  return [];
}

function pickLabel(it) {
  return String(it?.subject ?? it?.name ?? it?.value ?? "").trim();
}

function pickTotal(it) {
  const n = Number(it?.total ?? it?.count ?? it?.qty ?? it?.value_total ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const toNum = (v) => (Number.isFinite(+v) ? +v : 0);

export function mapTagsToWordcloud(raw, topN = 10) {
  const arr = normalizeToArray(raw);

  return arr
    .map((it) => ({ name: pickLabel(it), weight: pickTotal(it) }))
    .filter((x) => x.name && x.weight > 0)
    .sort((a, b) => b.weight - a.weight)
    .slice(0, topN);
}

export function mapTagsToTable(raw) {
  const arr = normalizeToArray(raw);

  return arr
    .map((it) => ({ subject: pickLabel(it), total: pickTotal(it) }))
    .filter((x) => x.subject)
    .sort((a, b) => b.total - a.total);
}

export function mapSubjectsQualitativeToStacked(raw, topN = 10) {
  const arr = normalizeToArray(raw);

  const rows = arr
    .map((it) => ({
      subject: pickLabel(it),
      total: toNum(it?.total),
      positive: toNum(it?.positive),
      neutral: toNum(it?.neutral),
      negative: toNum(it?.negative),
    }))
    .filter((x) => x.subject)
    .sort((a, b) => b.total - a.total)
    .slice(0, topN);

  return {
    categories: rows.map((r) => r.subject),
    positive: rows.map((r) => r.positive),
    neutral: rows.map((r) => r.neutral),
    negative: rows.map((r) => r.negative),
    total: rows.map((r) => r.total),
    totalsSum: rows.reduce((acc, r) => acc + r.total, 0),
    posSum: rows.reduce((acc, r) => acc + r.positive, 0),
    neuSum: rows.reduce((acc, r) => acc + r.neutral, 0),
    negSum: rows.reduce((acc, r) => acc + r.negative, 0),
  };
}

export function mapProtagonismBySubjectToSankey(raw, topN = 10) {
  const arr = Array.isArray(raw) ? raw : (raw?.results || []);
  const rows = arr
    .filter((it) => it?.subject)
    .sort((a, b) => toNum(b?.total) - toNum(a?.total))
    .slice(0, topN);

  const ignore = new Set([
    "subject", "total", "total_evaluated", "positive", "neutral", "negative",
    "id", "name", "value"
  ]);

  const keys = new Set();
  rows.forEach((it) => {
    Object.keys(it || {}).forEach((k) => {
      if (!ignore.has(k)) keys.add(k);
    });
  });

  const protagonismKeys = Array.from(keys);

  const links = [];
  for (const it of rows) {
    const subject = String(it.subject);
    for (const k of protagonismKeys) {
      const v = toNum(it[k]);
      if (v > 0) {
        links.push({
          source: k,
          target: subject,
          value: v,
        });
      }
    }
  }

  return { links, protagonismKeys, subjects: rows.map(r => r.subject) };
}

export function mapSubjectsPerDayToStackedSeries(raw, topN = 8, options = {}) {
  if (!raw || typeof raw !== "object") {
    return { categories: [], series: [] };
  }

  const { groupByMonth = false } = options;

  const buckets = groupByMonth
    ? Array.from(
        Object.entries(raw).reduce((acc, [date, subjects]) => {
          const month = getMonthBucket(date);
          if (!month) return acc;

          const current = acc.get(month.key) || { label: month.label, values: {} };

          Object.entries(subjects || {}).forEach(([subject, count]) => {
            current.values[subject] = (current.values[subject] || 0) + Number(count || 0);
          });

          acc.set(month.key, current);
          return acc;
        }, new Map())
      )
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([, value]) => value)
    : sortParsedDates(Object.keys(raw)).map(({ date }) => ({
        label: date,
        values: raw[date] || {},
      }));

  if (!buckets.length) return { categories: [], series: [] };

  const totals = {};
  for (const bucket of buckets) {
    const obj = bucket.values || {};
    for (const [subject, count] of Object.entries(obj)) {
      totals[subject] = (totals[subject] || 0) + Number(count || 0);
    }
  }

  const topSubjects = Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([name]) => name);

  const series = [];

  for (const subject of topSubjects) {
    series.push({
      name: subject,
      data: buckets.map((bucket) => Number(bucket.values?.[subject] || 0)),
    });
  }

  series.push({
    name: "Outros",
    data: buckets.map((bucket) => {
      const obj = bucket.values || {};
      let dayTotal = 0;
      for (const v of Object.values(obj)) dayTotal += Number(v || 0);

      let topSum = 0;
      for (const s of topSubjects) topSum += Number(obj?.[s] || 0);

      return Math.max(0, dayTotal - topSum);
    }),
  });

  return { categories: buckets.map((bucket) => bucket.label), series };
}

export function mapTopVehiclesQualitativeToStackedBar(raw = [], topN = 10) {
  const arr = Array.isArray(raw) ? raw : [];

  const top = [...arr]
    .sort((a, b) => Number(b?.total || 0) - Number(a?.total || 0))
    .slice(0, topN);

  const categories = top.map((it) => String(it?.name || it?.id || "—"));

  const positive = top.map((it) => Number(it?.positive || 0));
  const neutral = top.map((it) => Number(it?.neutral || 0));
  const negative = top.map((it) => Number(it?.negative || 0));

  return { categories, positive, neutral, negative };
}
